import React from 'react';
import {
  Card, Badge, Button, Form, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { formatBookingDateOnly, formatBookingTimeOnly } from '../../../Utils/timezone';
import { BOOKING_TYPE_LABELS, operatorRoleBadge } from '../../../config/bookingConstants';

// Helper function to get status badge color
function getStatusColor(status) {
  switch (status) {
    case 'Confirmed':
      return 'success';
    case 'Completed':
      return 'primary';
    case 'Cancelled':
      return 'danger';
    case 'Request':
      return 'warning';
    case 'Driver No-Show':
    case 'Passenger No-Show':
      return 'secondary';
    // NOT 'light'. This is a `<Badge bg>` variant, and react-bootstrap emits
    // it as `bg-light` without touching the colour, so the badge inherits
    // `--bs-badge-color: #fff` — white on #f8f9fa, 1.05:1. The statuses that
    // land here are real ones this switch has never covered ('Pending',
    // 'Pending Ingestion', 'Ingestion Failed'), so the default is what a
    // partner-ingestion booking on an event timeline actually renders as.
    // Same invisible-badge bug as Trello M5H44AvS; see
    // bookingStatusBadgeClass in Utils/bookingStatus.js, which is where this
    // map should eventually be folded.
    default:
      return 'secondary';
  }
}

function EventTimeline({
  groupedBookings,
  selectedBookings,
  onToggleSelection,
  onSelectAll,
  onEditBooking,
  onAssignOperator,
  onAssignDriver,
  onShowFlightDetails,
  onBatchAssignOperator,
  onBatchAssignDriver,
  onSearchLinkedBookings,
}) {
  // Sort groups by earliest pickup time
  const sortedGroups = Object.entries(groupedBookings).sort((a, b) => {
    const aTime = a[1][0]?.date_datetime || '';
    const bTime = b[1][0]?.date_datetime || '';
    return new Date(aTime) - new Date(bTime);
  });

  // eslint-disable-next-line no-underscore-dangle
  const allBookingIds = Object.values(groupedBookings).flat().map((b) => b._id);
  const allSelected = allBookingIds.length > 0 && allBookingIds.every(
    (id) => selectedBookings.includes(id),
  );

  return (
    <div className="event-timeline">
      {/* Header with select all */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className="fa fa-stream me-2" />
          Timeline View
        </h5>
        <Form.Check
          type="checkbox"
          label="Select All"
          checked={allSelected}
          onChange={onSelectAll}
        />
      </div>

      {sortedGroups.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <i className="fa fa-inbox fa-3x text-muted mb-3" />
            <h5 className="text-muted">No bookings found</h5>
            <p className="text-muted">Create a new booking to get started.</p>
          </Card.Body>
        </Card>
      ) : (
        sortedGroups.map(([groupName, bookings]) => (
          <Card key={groupName} className="mb-3 timeline-group">
            <Card.Header className={`d-flex justify-content-between align-items-center ${
              groupName === 'Ungrouped' ? 'bg-secondary-subtle' : 'bg-primary-subtle'
            }`}
            >
              <div className="d-flex align-items-center">
                <Badge
                  bg={groupName === 'Ungrouped' ? 'secondary' : 'primary'}
                  className="me-2"
                >
                  {bookings.length}
                </Badge>
                <span className="fw-bold">{groupName}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted">
                  {bookings.reduce((sum, b) => sum + (b.passengers?.amount || 0), 0)}
                  {' '}
                  passengers
                </small>
                {groupName !== 'Ungrouped' && bookings[0]?.date_datetime && (
                  <Badge bg="light" text="dark">
                    {formatBookingDateOnly(bookings[0], 'date_datetime')}
                    {' '}
                    {formatBookingTimeOnly(bookings[0], 'date_datetime')}
                  </Badge>
                )}
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>View flight details</Tooltip>}
                >
                  <Button
                    size="sm"
                    variant="outline-info"
                    onClick={() => onShowFlightDetails(groupName, bookings)}
                  >
                    <i className="fa fa-plane" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Batch assign operator</Tooltip>}
                >
                  <Button
                    size="sm"
                    variant="outline-warning"
                    onClick={() => onBatchAssignOperator(bookings)}
                  >
                    <i className="fa fa-building" />
                    <i className="fa fa-plus ms-1" style={{ fontSize: '0.6em' }} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Batch assign driver</Tooltip>}
                >
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => onBatchAssignDriver(bookings)}
                  >
                    <i className="fa fa-car" />
                    <i className="fa fa-plus ms-1" style={{ fontSize: '0.6em' }} />
                  </Button>
                </OverlayTrigger>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '40px' }} />
                      <th>Passenger</th>
                      <th>Time</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Pax</th>
                      <th>Operator / Driver</th>
                      <th>Status</th>
                      <th style={{ width: '140px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* eslint-disable-next-line no-underscore-dangle */}
                    {bookings.map((booking) => (
                      <tr
                        // eslint-disable-next-line no-underscore-dangle
                        key={booking._id}
                        // eslint-disable-next-line no-underscore-dangle
                        className={selectedBookings.includes(booking._id) ? 'table-active' : ''}
                      >
                        <td>
                          <Form.Check
                            type="checkbox"
                            // eslint-disable-next-line no-underscore-dangle
                            checked={selectedBookings.includes(booking._id)}
                            // eslint-disable-next-line no-underscore-dangle
                            onChange={() => onToggleSelection(booking._id)}
                          />
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <span className="fw-bold">{booking.full_name || '-'}</span>
                            {(() => {
                              const typeInfo = BOOKING_TYPE_LABELS[booking.booking_type]
                                || BOOKING_TYPE_LABELS.transfer;
                              return (
                                <Badge
                                  bg={typeInfo.color}
                                  style={{ fontSize: '0.6em' }}
                                  title={typeInfo.label}
                                >
                                  <i className={`fa ${typeInfo.icon}`} />
                                </Badge>
                              );
                            })()}
                            {booking.linked_booking_ids?.length > 0 && onSearchLinkedBookings && (
                              <OverlayTrigger
                                placement="top"
                                overlay={(
                                  <Tooltip>
                                    {booking.linked_booking_ids.length}
                                    {' '}
                                    linked booking(s) - click to search
                                  </Tooltip>
                                )}
                              >
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 ms-1"
                                  onClick={() => onSearchLinkedBookings(booking)}
                                >
                                  <i className="fa fa-link text-primary" />
                                </Button>
                              </OverlayTrigger>
                            )}
                          </div>
                          {booking.passenger_manifest?.length > 0 && (
                            <small className="text-info">
                              <i className="fa fa-users me-1" />
                              +
                              {booking.passenger_manifest.length}
                              {' '}
                              manifest
                            </small>
                          )}
                          {booking.flight_number && (
                            <small className="text-muted d-block">
                              <i className="fa fa-plane me-1" />
                              {booking.flight_number}
                            </small>
                          )}
                        </td>
                        <td>
                          {booking.date_datetime ? (
                            <div>
                              <div className="fw-bold">
                                {formatBookingTimeOnly(booking, 'date_datetime')}
                              </div>
                              <small className="text-muted">
                                {formatBookingDateOnly(booking, 'date_datetime')}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <small className="text-truncate d-block" style={{ maxWidth: '150px' }}>
                            {booking.destination_from?.address || '-'}
                          </small>
                        </td>
                        <td>
                          <small className="text-truncate d-block" style={{ maxWidth: '150px' }}>
                            {booking.destination_to?.address || '-'}
                          </small>
                        </td>
                        <td>
                          <Badge bg="light" text="dark">
                            {booking.passengers?.amount || 0}
                          </Badge>
                        </td>
                        <td>
                          {(() => {
                            // Multi-operator display for experiences
                            if (booking.booking_type === 'experience' && booking.operators?.length > 0) {
                              return (
                                <div>
                                  {booking.operators.map((op) => {
                                    const roleInfo = operatorRoleBadge(op.role, op.operator);
                                    const opKey = `${op.role}-${op.operator_id || 'unassigned'}`;
                                    return (
                                      <div key={opKey} className="mb-1">
                                        <Badge
                                          bg={roleInfo.color}
                                          className="me-1"
                                          style={{ fontSize: '0.65em' }}
                                        >
                                          <i className={`fa ${roleInfo.icon}`} />
                                        </Badge>
                                        <small className="fw-bold">
                                          {op.operator?.name || 'Unassigned'}
                                        </small>
                                        {op.driver?.full_name && (
                                          <small className="text-muted d-block ps-3">
                                            {op.driver.full_name}
                                          </small>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }
                            // Legacy single operator display
                            if (booking.operator?.name) {
                              return (
                                <div>
                                  <div className="fw-bold">
                                    <i className="fa fa-building me-1 text-muted" />
                                    {booking.operator.name}
                                  </div>
                                  {booking.driver?.full_name && (
                                    <small className="text-success d-block">
                                      <i className="fa fa-user me-1" />
                                      {booking.driver.full_name}
                                      {booking.driver.phone_number && (
                                        <span className="text-muted ms-1">
                                          (
                                          {booking.driver.phone_number}
                                          )
                                        </span>
                                      )}
                                    </small>
                                  )}
                                  {booking.car?.plate_number && (
                                    <small className="text-info d-block">
                                      <i className="fa fa-car me-1" />
                                      {booking.car.plate_number}
                                      {booking.car.type && ` - ${booking.car.type}`}
                                      {booking.car.color && ` (${booking.car.color})`}
                                    </small>
                                  )}
                                </div>
                              );
                            }
                            return <span className="text-muted">-</span>;
                          })()}
                        </td>
                        <td>
                          <Badge bg={getStatusColor(booking.status)}>
                            {booking.status || 'Unknown'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Edit booking</Tooltip>}
                            >
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => onEditBooking(booking)}
                              >
                                <i className="fa fa-edit" />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger
                              placement="top"
                              overlay={(
                                <Tooltip>
                                  {booking.operator_id ? 'Change operator' : 'Assign operator'}
                                </Tooltip>
                              )}
                            >
                              <Button
                                size="sm"
                                variant={booking.operator_id ? 'outline-success' : 'outline-warning'}
                                onClick={() => onAssignOperator(booking)}
                              >
                                <i className="fa fa-building" />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger
                              placement="top"
                              overlay={(
                                <Tooltip>
                                  {booking.driver ? 'Change driver' : 'Assign driver'}
                                </Tooltip>
                              )}
                            >
                              <span className="d-inline-block">
                                <Button
                                  size="sm"
                                  variant={booking.driver ? 'outline-success' : 'outline-secondary'}
                                  onClick={() => onAssignDriver(booking)}
                                  disabled={!booking.operator_id}
                                  style={!booking.operator_id ? { pointerEvents: 'none' } : {}}
                                >
                                  <i className="fa fa-car" />
                                </Button>
                              </span>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        ))
      )}
    </div>
  );
}

export default EventTimeline;
